import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useProject } from "../../context/ProjectContext";
import AdminLayout from "../../components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import {
  FileText,
  CheckCircle,
  Clock,
  Users,
  UserPlus,
  TrendingUp
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ title, value, icon: Icon, color, loading }) => (
  <Card className="border-0 shadow-sm" data-testid={`stat-${title.toLowerCase().replace(/\s/g, "-")}`}>
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-[#64748B]">{title}</p>
          {loading ? (
            <Skeleton className="h-8 w-16 mt-1" />
          ) : (
            <p className="text-3xl font-bold text-[#0F172A] mt-1">{value}</p>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const { getAuthHeaders } = useAuth();
  const { currentProject } = useProject();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentArticles, setRecentArticles] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = currentProject ? `?project_id=${currentProject.id}` : "";
        const [statsRes, articlesRes] = await Promise.all([
          axios.get(`${API}/stats/dashboard${params}`, { headers: getAuthHeaders() }),
          axios.get(`${API}/articles?limit=5${currentProject ? `&project_id=${currentProject.id}` : ""}`, { headers: getAuthHeaders() })
        ]);
        setStats(statsRes.data);
        setRecentArticles(articlesRes.data);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentProject, getAuthHeaders]);

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            title="Total Articles"
            value={stats?.total_articles || 0}
            icon={FileText}
            color="bg-[#3B82F6]"
            loading={loading}
          />
          <StatCard
            title="Published"
            value={stats?.published_articles || 0}
            icon={CheckCircle}
            color="bg-[#10B981]"
            loading={loading}
          />
          <StatCard
            title="Pending Review"
            value={stats?.pending_reviews || 0}
            icon={Clock}
            color="bg-[#F59E0B]"
            loading={loading}
          />
          <StatCard
            title="Writers"
            value={stats?.total_writers || 0}
            icon={Users}
            color="bg-[#8B5CF6]"
            loading={loading}
          />
          <StatCard
            title="Applications"
            value={stats?.pending_applications || 0}
            icon={UserPlus}
            color="bg-[#EC4899]"
            loading={loading}
          />
        </div>

        {/* Recent Articles */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="h-5 w-5 text-[#3B82F6]" />
              Recent Articles
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : recentArticles.length > 0 ? (
              <div className="divide-y divide-[#E2E8F0]">
                {recentArticles.map((article) => (
                  <div key={article.id} className="py-4 first:pt-0 last:pb-0" data-testid={`article-row-${article.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-[#0F172A] truncate">{article.title}</h4>
                        <div className="flex items-center gap-3 mt-1 text-sm text-[#64748B]">
                          <span>{article.author_name}</span>
                          <span>•</span>
                          <span>{article.category}</span>
                          <span>•</span>
                          <span>{new Date(article.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                        article.status === "published"
                          ? "bg-[#DCFCE7] text-[#166534]"
                          : article.status === "pending_review"
                          ? "bg-[#FEF3C7] text-[#92400E]"
                          : article.status === "declined"
                          ? "bg-[#FEE2E2] text-[#991B1B]"
                          : "bg-[#F1F5F9] text-[#475569]"
                      }`}>
                        {article.status.replace("_", " ")}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[#64748B]">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No articles yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
